import omitBy from 'lodash/omitBy';
import isUndefined from 'lodash/isUndefined';

import {isPersistedCustomProperty} from 'common/widgets/custom-property-modal/util';
import {reporter} from 'common/report/error-reporting';
import {MetricType} from 'common/query-builder/query-block/constants';
import {FUNNELS_MATH_TYPES, RETENTION_MATH_TYPES} from 'common/report/constants';
import {FUNNELS_TIME_TO_CONVERSION, PAGE_DURATION} from 'common/query-builder/query-block/properties-menu/constants';
import {FunnelOrder} from 'common/report/funnels/models/constants';
import {FunnelBookmark} from 'common/report/funnels/models/types';
import {FlowsBookmark} from 'common/report/flows/models/types';
import {RetentionBookmarkParams} from 'common/report/retention/util/types';
import type {MetricsResource} from 'common/report/model-resources/metrics';
import {getResolvedFormulaShowClause} from 'common/util/formulas';

import {
  BehaviorMetric,
  BehaviorShowClause as BehaviorShowClauseType,
  FormulaShowClause as FormulaShowClauseType,
  GroupClause as GroupClauseType,
  InsightsBookmarkParams,
  InsightsResourceType,
  isInsightsBookmarkParams,
  ShowClause as ShowClauseType,
  ShowClauseFormula,
} from '../util/types';

export function sanitizeIfCustomProperty(property: BehaviorMetric['measurement']['property']) {
  if (!property.customPropertyId) {
    return property;
  }

  const {customProperty, ...rest} = property;
  return {
    ...rest,
    // customProperty (custom property definition) should not be passed as an attr for persisted
    // custom properties. However, old bookmark did store customProperty for persisted custom
    // properties, which we remove below.
    ...(!isPersistedCustomProperty(property.customPropertyId) && customProperty ? {customProperty} : {}),
  };
}

export function isBehaviorShowClause(showClause: ShowClauseType): showClause is BehaviorShowClauseType {
  if (`behavior` in showClause && `measurement` in showClause) {
    return true;
  }

  if (`behavior` in showClause) {
    reporter.error(`Non-behavior show clause has unexpected "behavior" key`, {showClause});
  }
  return false;
}

export function isFormulaShowClause(showClause: ShowClauseType): showClause is FormulaShowClauseType {
  return `formula` in showClause;
}

export function getBehaviorShowClauses(showClauses: ShowClauseType[]): BehaviorShowClauseType[] {
  if (Array.isArray(showClauses) === false) {
    return [];
  }

  return showClauses.filter(isBehaviorShowClause);
}

/**
 * Get BehaviorShowClauses plus any that are in FormulaShowClause.formula.referencedMetrics
 * Note: this function is similar to the method getExpandedBehaviorClauses on ShowSection
 * in ../section.ts (which operates on class types)
 */
export function getExpandedBehaviorShowClauses({
  showClauses,
  metricsResource,
}: {
  showClauses: ShowClauseType[];
  metricsResource: MetricsResource;
}): BehaviorShowClauseType[] {
  if (Array.isArray(showClauses) === false) {
    return [];
  }

  return showClauses
    .map((showClause) => {
      if (isBehaviorShowClause(showClause)) {
        return showClause;
      } else {
        const resolved = getResolvedFormulaShowClause({formulaShowClause: showClause, metricsResource});
        return resolved?.formula?.referencedMetrics || [];
      }
    })
    .flat();
}

export function hasGroupByCohort(group: GroupClauseType[]): boolean {
  return group.some((groupClause) => groupClause.cohorts);
}

export function hasGroupByDuration(group: GroupClauseType[]): boolean {
  return group.some((groupClause) => groupClause.value === PAGE_DURATION.value);
}

export function hasGroupByUser(group: GroupClauseType[]): boolean {
  return group.some((groupClause) => groupClause.resourceType === InsightsResourceType.People);
}

export function hasRolling(showClauses: ShowClauseType[]): boolean {
  return showClauses.some((clause) => clause.measurement?.rolling);
}

export function hasCumulative(showClauses: ShowClauseType[]): boolean {
  return showClauses.some((clause) => clause.measurement?.cumulative);
}

export function getNumerOfEventComparisons(showClause: ShowClauseType): number {
  if (!showClause) return 0;

  const behaviors = isBehaviorShowClause(showClause) ? showClause.behavior?.behaviors : [];
  return (behaviors || []).reduce((acc, beh) => {
    if (`behaviors` in beh) {
      return acc + 1;
    }
    return acc;
  }, 0);
}

export function hasAnyOrderFunnel(showClauses: ShowClauseType[]) {
  return (
    showClauses.some((clause) => isBehaviorShowClause(clause) && clause.behavior.type === MetricType.Funnel) &&
    showClauses.some(
      (clause) =>
        isBehaviorShowClause(clause) &&
        clause.behavior.behaviors?.some((behavior) => behavior.funnelOrder === FunnelOrder.Any),
    )
  );
}

export function hasTTC(showClauses: ShowClauseType[]) {
  return showClauses.some(
    (clause) =>
      isBehaviorShowClause(clause) &&
      clause.measurement.property &&
      clause.measurement.property.name === `$time_to_conversion`,
  );
}

export function getFormulaShowClauses(showClauses: ShowClauseType[]): FormulaShowClauseType[] {
  return showClauses.filter(isFormulaShowClause);
}

export function hasSavedFormula(params: InsightsBookmarkParams) {
  return params.sections.show.some((clause) => {
    if (!isFormulaShowClause(clause)) {
      return false;
    }

    return Boolean(clause.formula.id);
  });
}

export function isValidFormula(formula: ShowClauseFormula): boolean {
  if (formula.id) {
    return true;
  }

  return Boolean(formula && formula.name && formula.definition);
}

export function transformBookmarkToMultiMetricBookmark(bookmarkParams: InsightsBookmarkParams): InsightsBookmarkParams {
  // defensive programming, just in case the params pass in is falsy
  if (!bookmarkParams?.sections) return bookmarkParams;
  return {
    ...bookmarkParams,
    sections: {
      ...bookmarkParams.sections,
      cohorts: [],
      group: moveCohortToGroupSection(bookmarkParams),
      show: bookmarkParams.sections.show.map((show) => {
        if (isFormulaShowClause(show)) {
          return {
            ...(show.isHidden ? {isHidden: true} : {}),
            ...(show.isExpanded ? {isExpanded: true} : {}),
            formula: {...show.formula},
            measurement: {...show.measurement},
          };
        } else {
          const {behavior, measurement} = transformShowToBehaviorMetric(show);
          return {
            ...(show.isHidden ? {isHidden: true} : {}),
            ...(show.isExpanded ? {isExpanded: true} : {}),
            behavior: {
              type: MetricType.Event,
              resourceType: behavior.resourceType,
              ...omitBy(behavior, isUndefined),
            },
            measurement: {
              math: measurement.math,
              ...omitBy(measurement, isUndefined),
            },
          };
        }
      }),
    },
  };
}

export function moveCohortToGroupSection(bookmarkParams: InsightsBookmarkParams) {
  const cohortsSection = bookmarkParams.sections?.cohorts || [];
  // Place cohorts at the top level in group section
  const cohorts = [];
  cohortsSection.forEach((cohort) => {
    cohorts.push(cohort);
  });

  const groupSection = bookmarkParams.sections?.group || [];
  if (cohorts.length > 0) {
    const value = cohorts.map((cohort) => cohort.name);
    const groupByCohorts: GroupClauseType = {
      value,
      cohorts,
      resourceType: InsightsResourceType.Events,
    };
    return [groupByCohorts, ...groupSection];
  }

  return groupSection;
}

export function transformShowToBehaviorMetric(show: BehaviorShowClauseType): BehaviorMetric {
  if (show.behavior && show.measurement) {
    return {
      behavior: show.behavior,
      measurement: show.measurement,
    };
  }
  const {
    math,
    perUserAggregation,
    property,
    rolling,
    cumulative,
    segmentMethod,
    multiAttribution,
    search,
    resourceType,
    profileType,
    value,
    filter,
    filterDeterminer,
    dataGroupId,
    dataset,
    datasetId,
    projectId,
    customBucket,
  } = show;
  // for original show clause value, it could only represent event or custom event or cohort
  const {name, custom, id, raw_cohort: rawCohort, renamed, resourceType: valueResourceType} = value || {};
  const type = custom
    ? MetricType.CustomEvent
    : valueResourceType === InsightsResourceType.Cohorts || resourceType === InsightsResourceType.Cohorts
    ? MetricType.Cohort
    : resourceType === InsightsResourceType.People
    ? MetricType.People
    : MetricType.Event;

  return {
    measurement: {
      math,
      property,
      cumulative,
      perUserAggregation,
      rolling,
      segmentMethod,
      multiAttribution,
    },
    behavior: {
      type,
      id,
      name,
      renamed,
      raw_cohort: rawCohort,
      search,
      resourceType,
      profileType,
      filters: filter,
      filtersDeterminer: filterDeterminer,
      dataGroupId,
      dataset,
      datasetId,
      projectId,
      customBucket,
    },
  };
}

export function isFunnelConversionShowClause(show: ShowClauseType): boolean {
  return (
    isBehaviorShowClause(show) &&
    show.behavior?.type === MetricType.Funnel &&
    [
      FUNNELS_MATH_TYPES.CONVERSION_RATE_UNIQUE,
      FUNNELS_MATH_TYPES.CONVERSION_RATE_TOTAL,
      FUNNELS_MATH_TYPES.CONVERSION_RATE_SESSION,
    ].includes(show.measurement.math as FUNNELS_MATH_TYPES)
  );
}

export function isFunnelTimeToConvertShowClause(show: ShowClauseType): boolean {
  return (
    isBehaviorShowClause(show) &&
    ((show.behavior?.type === MetricType.Funnel &&
      show.measurement.property?.name === FUNNELS_TIME_TO_CONVERSION.value) ||
      ((show.behavior?.type === MetricType.Event || show.behavior?.type === MetricType.CustomEvent) &&
        show.measurement.property?.name === PAGE_DURATION.value))
  );
}

export function isRetentionConversionRateShowClause(show: ShowClauseType): boolean {
  return (
    isBehaviorShowClause(show) &&
    [MetricType.Retention, MetricType.RetentionFrequency].includes(show.behavior?.type) &&
    show.measurement.math === RETENTION_MATH_TYPES.RETENTION_RATE
  );
}

export function isFunnelConversionMetric(bookmark: InsightsBookmarkParams): boolean {
  return bookmark.sections.show.every(isFunnelConversionShowClause);
}

export function isFunnelTimeToConvertMetric(bookmark: InsightsBookmarkParams): boolean {
  return bookmark.sections.show.every(isFunnelTimeToConvertShowClause);
}

export function isRetentionConversionRateMetric(bookmark: InsightsBookmarkParams): boolean {
  return bookmark.sections.show.every(isRetentionConversionRateShowClause);
}

export function isFunnelOnlyInsightsBookmark(
  bookmark: InsightsBookmarkParams | FunnelBookmark | FlowsBookmark | RetentionBookmarkParams,
): boolean {
  return (
    isInsightsBookmarkParams(bookmark) &&
    bookmark.sections.show.length === 1 &&
    bookmark.sections.show.every(isBehaviorShowClause) &&
    bookmark.sections.show[0].behavior.type === MetricType.Funnel
  );
}

export function isRetentionOnlyInsightsBookmark(
  bookmark: InsightsBookmarkParams | FunnelBookmark | FlowsBookmark | RetentionBookmarkParams,
): boolean {
  return (
    isInsightsBookmarkParams(bookmark) &&
    bookmark.sections.show.length === 1 &&
    bookmark.sections.show.every(isBehaviorShowClause) &&
    [MetricType.Retention, MetricType.RetentionFrequency].includes(bookmark.sections.show[0].behavior.type)
  );
}
